// Whether a terminal puts what is selected in it on the clipboard as the
// mouse is let go, as iTerm does (Poulpe-236): on unless Settings › General
// says not to, and remembered on this device, as the theme is (theme.ts).
//
// `board.terminal.copySelection` in localStorage is `off` when it is off; no
// key is on. A terminal reads it as each selection is made, so a change holds
// at once in every terminal open, on this page or another.
import { useSyncExternalStore } from 'react'

export const COPY_SELECTION_KEY = 'board.terminal.copySelection'

// A choice that storage would not keep (a private window): it holds on this
// page until it is reloaded.
let unstored: boolean | null = null

export function copiesSelection(): boolean {
  if (unstored !== null) return unstored
  try {
    return window.localStorage.getItem(COPY_SELECTION_KEY) !== 'off'
  } catch {
    return true
  }
}

const listeners = new Set<() => void>()

export function setCopiesSelection(on: boolean): void {
  try {
    if (on) window.localStorage.removeItem(COPY_SELECTION_KEY)
    else window.localStorage.setItem(COPY_SELECTION_KEY, 'off')
    unstored = null
  } catch {
    unstored = on
  }
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  // Another page of the board chose: a browser tells every page of an origin but the one that wrote.
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== COPY_SELECTION_KEY) return
    unstored = null
    listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

/** The choice, kept current as it is made, on this page or on another. */
export function useCopiesSelection(): boolean {
  return useSyncExternalStore(subscribe, copiesSelection, () => true)
}
